import React, { useEffect, useState } from "react";
import "@/index.css";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Inquiries from "@/pages/Inquiries";
import Pipeline from "@/pages/Pipeline";
import Products from "@/pages/Products";
import Match from "@/pages/Match";
import Reports from "@/pages/Reports";
import Layout from "@/components/saheli/Layout";
import { exchangeSession, fetchMe } from "@/lib/auth";

function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const hasProcessed = React.useRef(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }
    exchangeSession(sessionId)
      .then((user) => {
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true, state: { user } });
      })
      .catch((e) => {
        console.error(e);
        const detail = e?.response?.data?.detail;
        const isDenied = e?.response?.status === 403;
        setError(
          isDenied
            ? detail || "This account is not on the admin list."
            : "Login failed",
        );
        setTimeout(
          () =>
            navigate("/login", {
              replace: true,
              state: { authError: detail || "Login failed" },
            }),
          isDenied ? 2200 : 1200,
        );
      });
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        {error ? (
          <>
            <div className="text-terracotta font-serif text-2xl mb-2">
              Access denied
            </div>
            <div className="text-stone-500 text-sm">{error}</div>
          </>
        ) : (
          <div className="text-stone-500 text-sm">Signing you in…</div>
        )}
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  const passedUser = location.state?.user || null;
  const [state, setState] = useState(passedUser ? "auth" : "checking");
  const [user, setUser] = useState(passedUser);

  useEffect(() => {
    if (passedUser) return;
    // If we're returning from OAuth, defer to AuthCallback
    if (window.location.hash?.includes("session_id=")) return;
    let cancelled = false;
    fetchMe()
      .then((u) => {
        if (cancelled) return;
        setUser(u);
        setState("auth");
      })
      .catch(() => {
        // DEMO MODE: never block the user. fetchMe already fails-open, but keep
        // a hard safety net so no one lands on /login unexpectedly.
        if (cancelled) return;
        setUser({ email: "demo@silkmartsarees.com", name: "Silk Mart Admin" });
        setState("auth");
      });
    return () => {
      cancelled = true;
    };
  }, [passedUser]);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-stone-500 text-sm">Loading…</div>
      </div>
    );
  }
  return <Layout user={user}>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inquiries"
        element={
          <ProtectedRoute>
            <Inquiries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute>
            <Pipeline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Products />
          </ProtectedRoute>
        }
      />
      <Route
        path="/match"
        element={
          <ProtectedRoute>
            <Match />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}
